import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  InputLabel,
  InputAdornment,
  Autocomplete,
  Divider,
  Stack,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import ProfilePhotoManager from '../ProfilePhotoManager';
import { getDocs, query, collection, where, doc } from 'firebase/firestore';
import { useFirebase } from '../../contexts/FirebaseContext';
import iconMembership from '../../assets/images/icon-membership.svg';
import { StatusDisplay } from './StatusDisplay';
import { useUserUtils } from '../../utilities/UserUtils';
const createSearchableString = (str) => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const ProfileSection = ({ handleResendVerification, loading, onSubmit }) => {
  const { userProfile } = useAuth();
  console.log('currentUser', userProfile);
  // const [username, setUsername] = useState(userProfile?.username || '');
  const [isOrganizerStatus, setIsOrganizerStatus] = useState(false);
  const [homeClub, setHomeClub] = useState(userProfile?.homeClub || '');
  const [clubSuggestions, setClubSuggestions] = useState([]);
  const { checkUserNameAvailable } = useUserUtils();
  const [userNameValidation, setUserNameValidation] = useState(false);

  const [formData, setFormData] = useState({
    firstName: userProfile?.firstName || '',
    lastName: userProfile?.lastName || '',
    username: userProfile?.username || '',
    homeClub: userProfile?.homeClub || '',
    homeClubId: userProfile?.homeClubId || '',
    handicap: userProfile?.handicap || '',
  });
  const { db } = useFirebase();
  useEffect(() => {
    if (userProfile?.uid) {
      let games = [];
      const isOrganizer = async () => {
        const userRef = doc(db, 'users', userProfile?.uid);
        const gamesRef = collection(db, 'games');
        const q = query(gamesRef, where('createdBy', '==', userRef));
        const querySnapshot = await getDocs(q);

        querySnapshot.forEach((doc) => {
          games.push(doc.data());
        });
        console.log('games', games);
        setIsOrganizerStatus(games.length > 0);
      };
      isOrganizer();
    }
  }, [userProfile?.uid, db]);

  const fetchClubSuggestions = useCallback(
    async (searchTerm) => {
      const clubsRef = collection(db, 'clubs');
      const searchableSearchTerm = createSearchableString(searchTerm);
      const clubsQuery = query(clubsRef, where('searchableClubName', '>=', searchableSearchTerm));
      const clubSnapshot = await getDocs(clubsQuery);
      const clubList = clubSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setClubSuggestions(clubList);
    },
    [db]
  );

  useEffect(() => {
    if (homeClub.length === 1) {
      fetchClubSuggestions(homeClub);
    }
  }, [homeClub, fetchClubSuggestions]);

  const handleClubChange = (val) => {
    if (val) {
      setFormData((prev) => ({
        ...prev,
        homeClub: val.clubName,
        homeClubId: val.id,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        homeClub: '',
        homeClubId: '',
      }));
    }
  };

  const handleUsernameChange = async (e) => {
    const { value } = e.target;
    const usernameAvailable = await checkUserNameAvailable(value);
    setUserNameValidation(usernameAvailable);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleUsernameChange(e);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const ProfileSubhead = ({ icon, label, tag, align }) => {
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', justifyContent: align ? align : 'center' }}
      >
        <img src={icon} alt={`${label} icon`} />
        <Typography
          gutterBottom
          component={tag ? tag : 'strong'}
          variant="small"
          sx={{ fontWeight: '600' }}
        >
          {label}
        </Typography>
      </Stack>
    );
  };

  return (
    <>
      <Box variant="settingSection">
        <Typography component="h3" variant="sectionHeader">
          Your Profile
        </Typography>

        <Typography variant="formHeader">Name &amp; Image</Typography>

        <ProfilePhotoManager />
        {isOrganizerStatus && <ProfileSubhead icon={iconMembership} label="Golf Organizer" />}
        {/* Username field */}
        <InputLabel htmlFor="username" sx={{ fontWeight: '700' }}>
          User Name
        </InputLabel>
        <TextField
          name="username"
          value={formData.username}
          onChange={handleInputChange}
          disabled={loading}
          fullWidth
          sx={{ mb: 2, mt: 1 }}
          variant="outlined"
        />
        {/* First Name field */}
        <InputLabel htmlFor="firstName" sx={{ fontWeight: '700' }}>
          First Name
        </InputLabel>
        <TextField
          name="firstName"
          value={formData.firstName}
          onChange={handleInputChange}
          disabled={loading}
          fullWidth
          sx={{ mb: 2, mt: 1 }}
          variant="outlined"
        />

        {/* Last Name field */}
        <InputLabel htmlFor="lastName" sx={{ fontWeight: '700' }}>
          Last Name
        </InputLabel>
        <TextField
          name="lastName"
          value={formData.lastName}
          onChange={handleInputChange}
          disabled={loading}
          fullWidth
          sx={{ mb: 2, mt: 1 }}
          variant="outlined"
        />

        {userNameValidation && !userNameValidation.valid && (
          <Typography variant="body2" sx={{ color: 'red', mt: 0 }}>
            {userNameValidation.message}
          </Typography>
        )}
        {userNameValidation &&
          !userNameValidation.valid &&
          userNameValidation.suggestedUserName && (
            <Typography variant="body2" sx={{ color: 'black', mt: 1, mb: 3 }}>
              Try:{' '}
              <strong
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    username: userNameValidation.suggestedUserName,
                  }));
                  handleUsernameChange({
                    target: { value: userNameValidation.suggestedUserName },
                  });
                }}
              >
                {userNameValidation.suggestedUserName}
              </strong>
            </Typography>
          )}
        <InputLabel htmlFor="course" sx={{ fontWeight: '700' }}>
          Set your Home Course
        </InputLabel>
        <Autocomplete
          freeSolo
          disableClearable
          inputValue={homeClub}
          options={clubSuggestions}
          placeholder="Search for your home club"
          getOptionLabel={(option) => option.clubName}
          filterOptions={(options, { inputValue }) => {
            const normalizedInput = createSearchableString(inputValue);
            return options.filter((option) =>
              createSearchableString(option.searchableClubName).includes(normalizedInput)
            );
          }}
          onInputChange={(event, newInputValue) => setHomeClub(newInputValue)}
          onChange={(event, newValue) => handleClubChange(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              placeholder="Search for your home club"
              variant="outlined"
            />
          )}
          sx={{ mb: 2, mt: 1 }}
        />

        <InputLabel htmlFor="handicap" sx={{ fontWeight: '700' }}>
          What is your handicap?
        </InputLabel>
        <TextField
          type="number"
          fullWidth
          name="handicap"
          placeholder="Handicap"
          value={formData.handicap}
          onChange={handleInputChange}
          variant="outlined"
          slotProps={{ 
            inputLabel: { shrink: true },
            input: {
              inputProps: { min: 0.0, max: 54.0, step: 0.1 }
            }
          }}
          error={formData.handicap && (formData.handicap < 0.0 || formData.handicap > 54.0)}
          helperText={formData.handicap && (formData.handicap < 0.0 || formData.handicap > 54.0) ? "Handicap must be between 0.0 and 54.0" : ""}
          sx={{ mt: 1 }}
        />
        <Box sx={{ mt: 3 }}>
          <Button variant="text" onClick={() => {}} sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => onSubmit(formData)} disabled={loading}>
            Save
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box variant="settingSection">
        <Typography component="h3" variant="sectionHeader">
          Contact Info
        </Typography>
        {/* Email display */}
        <Box sx={{ mb: 2 }}>
          <InputLabel htmlFor="email" sx={{ fontWeight: '700' }}>
            Email
          </InputLabel>
          <Stack sx={{ mt: 1 }}>
            <TextField
              value={userProfile.email}
              name="email"
              disabled={userProfile?.emailVerified}
              variant="outlined"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {/* <StatusDisplay
                        isVerified={userProfile?.emailVerified}
                        verifiedText="Verified"
                      /> */}
                    </InputAdornment>
                  ),
                },
              }}
            />
            {/* {!userProfile?.emailVerified && (
              <Box sx={{ textAlign: 'left' }}>
                <Button
                  variant="link"
                  size="small"
                  onClick={handleResendVerification}
                  disabled={loading}
                  sx={{ ml: 1 }}
                >
                  Resend Verification?
                </Button>
              </Box>
            )} */}
          </Stack>
        </Box>

        {/* Phone number display */}
        <Box sx={{ mb: 2 }}>
          <a name="phone">
            <InputLabel htmlFor="phone" sx={{ fontWeight: '700', display: 'block' }}>
              Phone
            </InputLabel>
          </a>
          <Stack sx={{ mt: 1 }}>
            <TextField
              value={userProfile.phoneNumber || 'Not Linked'}
              name="phone"
              disabled={userProfile?.phoneVerified}
              variant="outlined"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {userProfile?.phoneVerified ? (
                        <StatusDisplay isVerified={true} verifiedText="Verified" />
                      ) : userProfile?.phone ? (
                        <StatusDisplay isVerified={false} verifiedText="Verified" />
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {}}
                          disabled={loading}
                          sx={{ ml: 1 }}
                        >
                          Link Number
                        </Button>
                      )}
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>To receive Wagl notifications and game invitations via WhatApp, you must provide a mobile phone number.</Typography>
          </Stack>
        </Box>
      </Box>
    </>
  );
};
